import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, QrCode, RefreshCw, CheckCircle2, Plus, Power, Smartphone } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import io from 'socket.io-client';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const WhatsAppConfig = ({ estabelecimentoId }) => {
    const [qrCode, setQrCode] = useState(null);
    // 🔥 MUDANÇA: Começa como 'verificando' para não mostrar o botão de QR antes da hora
    const [status, setStatus] = useState('verificando'); 
    const [device, setDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const socketRef = useRef(null);

    useEffect(() => {
        socketRef.current = io('https://app.universomidiadigital.com.br', {
            transports: ['websocket'],
            reconnection: true
        });

        socketRef.current.on('connect', () => {
            socketRef.current.emit('join', estabelecimentoId);
        });

        socketRef.current.on('whatsapp-qr', (qr) => {
            setQrCode(qr);
            setStatus('lendo');
            setLoading(false);
        });

        socketRef.current.on('whatsapp-status', (data) => {
            setStatus(data?.status || 'desconectado');
            if (data?.device) setDevice(data.device);

            if (data?.status === 'conectado') {
                setQrCode(null);
                toast.success("WhatsApp conectado com sucesso!");
            }
            
            if (data?.status === 'desconectado') {
                setDevice(null);
                setQrCode(null);
            }
            setLoading(false);
        });

        verificarStatusAtual();

        return () => socketRef.current?.disconnect();
    }, [estabelecimentoId]);

  const verificarStatusAtual = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/whatsapp/status/${estabelecimentoId}`);
            
            // 🔥 CAPTURA ROBUSTA: Verifica tanto na raiz quanto no payload
            const payload = res.data?.payload || res.data;
            const statusAtual = payload?.status || res.data?.status;
            const deviceAtual = payload?.device || res.data?.device;

            if (statusAtual === 'conectado' || statusAtual === 'connected') {
                setStatus('conectado');
                setDevice(deviceAtual || { name: 'WhatsApp Principal', number: 'Conectado' });
                setQrCode(null);
            } else {
                setStatus('desconectado');
                setDevice(null);
            }
        } catch (err) {
            console.error("Erro ao verificar status:", err);
            setStatus('desconectado');
        } finally {
            setLoading(false);
        }
    };

    const handleGerarQR = async () => {
        setLoading(true);
        setQrCode(null);
        try {
            await api.post(`/whatsapp/connect/${estabelecimentoId}`);
            toast.success("Solicitando QR Code...");
        } catch (err) {
            toast.error("Erro ao iniciar conexão");
            setLoading(false);
        }
    };

    const handleDesconectar = async () => {
        if (!window.confirm("Deseja desconectar seu WhatsApp?")) return;
        setLoading(true);
        try {
            await api.post(`/whatsapp/disconnect/${estabelecimentoId}`);
            setStatus('desconectado');
            setDevice(null);
            setQrCode(null);
            toast.success("Desconectado com sucesso!");
        } catch (err) {
            toast.error("Erro ao desconectar");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ws-container">
            <div className="ws-card">
                <div className="ws-header">
                    <div className="ws-title-group">
                        <div className="ws-main-icon"><MessageSquare size={24} /></div>
                        <div>
                            <h1>Integração WhatsApp</h1>
                            <p>Sincronização em tempo real via Universo Mídia</p>
                        </div>
                    </div>
                    <div className={`ws-badge ${status}`}>
                        <div className="dot"></div>
                        {status === 'conectado' ? 'Conectado' : status === 'lendo' ? 'Aguardando Leitura' : 'Desconectado'}
                    </div>
                </div>

                <div className="ws-content">
                    {status === 'conectado' && device ? (
                        <div className="ws-active-device">
                            <div className="ws-avatar-wrapper">
                                <img src={device.photo || 'https://ui-avatars.com/api/?name=WA&background=25D366&color=fff'} alt="perfil" />
                                <div className="ws-online-indicator"><CheckCircle2 size={16} /></div>
                            </div>
                            
                            <div className="ws-device-info">
                                <h2>{device.name || 'WhatsApp Conectado'}</h2>
                                <p><Smartphone size={14} /> +{device.number}</p>
                                <span className="ws-platform-tag">Sessão Ativa By Marcelo Rocha</span>
                            </div>

                            <button className="ws-btn-disconnect" onClick={handleDesconectar} disabled={loading}>
                                <Power size={18} />
                                Desconectar Aparelho
                            </button>
                        </div>
                    ) : (
                        <div className="ws-qr-container">
                            {qrCode ? (
                                <div className="ws-qr-box">
                                    <div style={{ background: 'white', padding: '15px', borderRadius: '10px' }}>
                                        <QRCodeCanvas value={qrCode} size={240} level="H" includeMargin={false} />
                                    </div>
                                    <div className="ws-qr-instructions">
                                        <div className="step">1. Abra o WhatsApp no seu celular</div>
                                        <div className="step">2. Vá em <b>Aparelhos Conectados</b></div>
                                        <div className="step">3. Escaneie o código acima</div>
                                    </div>
                                    <div className="ws-sync-loader">
                                        <RefreshCw className="spin" size={18} />
                                        Aguardando sincronização...
                                    </div>
                                </div>
                            ) : (
                                <div className="ws-empty-state">
                                    <div className="ws-empty-icon"><QrCode size={48} /></div>
                                    <h3>Nenhum aparelho conectado</h3>
                                    <p>Conecte seu WhatsApp para habilitar o chat em tempo real e envios automáticos.</p>
                                    <button className="ws-btn-primary" onClick={handleGerarQR} disabled={loading}>
                                        {loading ? <RefreshCw className="spin" size={20} /> : <Plus size={20} />}
                                        Gerar Novo QR Code
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .ws-container { padding: 20px; max-width: 800px; margin: 0 auto; font-family: sans-serif; }
                .ws-card { background: #fff; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); overflow: hidden; border: 1px solid #f0f0f0; }
                .ws-header { padding: 30px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f7f7f7; background: #fafafa; }
                .ws-title-group { display: flex; gap: 15px; align-items: center; }
                .ws-main-icon { background: #25d366; color: white; padding: 12px; border-radius: 12px; }
                .ws-header h1 { font-size: 20px; margin: 0; color: #1a1a1a; }
                .ws-header p { font-size: 13px; margin: 0; color: #666; }
                .ws-badge { display: flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
                .ws-badge.conectado { background: #e6fcf5; color: #0ca678; }
                .ws-badge.desconectado { background: #fff5f5; color: #fa5252; }
                .ws-badge.lendo { background: #fff9db; color: #f08c00; }
                .ws-badge .dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
                .ws-content { padding: 40px; min-height: 350px; display: flex; align-items: center; justify-content: center; }
                .ws-active-device { display: flex; flex-direction: column; align-items: center; text-align: center; width: 100%; }
                .ws-avatar-wrapper { position: relative; margin-bottom: 20px; }
                .ws-avatar-wrapper img { width: 100px; height: 100px; border-radius: 50%; border: 4px solid #fff; box-shadow: 0 4px 15px rgba(0,0,0,0.1); object-fit: cover; }
                .ws-online-indicator { position: absolute; bottom: 5px; right: 5px; background: #25d366; color: white; border-radius: 50%; padding: 4px; border: 3px solid #fff; display: flex; }
                .ws-device-info h2 { margin: 0; font-size: 22px; color: #1a1a1a; }
                .ws-device-info p { display: flex; align-items: center; justify-content: center; gap: 5px; color: #666; margin: 8px 0; font-weight: 500; }
                .ws-platform-tag { font-size: 11px; background: #f0f0f0; padding: 4px 12px; border-radius: 6px; color: #888; }
                .ws-btn-disconnect { margin-top: 30px; display: flex; align-items: center; gap: 10px; background: #fff; color: #fa5252; border: 2px solid #fa5252; padding: 12px 24px; border-radius: 12px; cursor: pointer; font-weight: 600; transition: 0.2s; }
                .ws-btn-disconnect:hover:not(:disabled) { background: #fa5252; color: #fff; }
                .ws-qr-box { display: flex; flex-direction: column; align-items: center; gap: 20px; }
                .ws-qr-instructions { text-align: left; background: #f8f9fa; padding: 20px; border-radius: 12px; width: 100%; border: 1px solid #eee; }
                .ws-qr-instructions .step { font-size: 14px; color: #444; margin-bottom: 8px; }
                .ws-sync-loader { display: flex; align-items: center; gap: 10px; color: #25d366; font-weight: 600; font-size: 14px; }
                .ws-empty-state { text-align: center; max-width: 400px; }
                .ws-empty-icon { color: #e0e0e0; margin-bottom: 20px; }
                .ws-btn-primary { background: #25d366; color: white; border: none; padding: 14px 28px; border-radius: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 10px; margin: 0 auto; transition: 0.3s; }
                .ws-btn-primary:hover:not(:disabled) { background: #1ebe5d; transform: translateY(-2px); box-shadow: 0 5px 15px rgba(37,211,102,0.3); }
                .ws-btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default WhatsAppConfig;